import { useMemo } from 'react';
import PassFailBackground, {
  Label,
  UnprofitableLabel,
} from '../../../PassFailBackground';
import { useTheme } from '@simplywallst/ui-core';
import { scaleLinear } from '@visx/scale';
import { MultipleVsIndustryChartSvg, Text } from './styled';
import { toSignificant } from '@/utilities/formatting';
import ChartInteractions from './components/ChartInteractions';
import { formatMultiple, getLabelWidth } from '../../../../utils';
import type { PreferredMultiple } from '../../../../selector';
import { useValuationTranslation } from '@/pages/CompanyReport/components/Valuation/hooks/useValuationTranslation';

interface Props {
  size: any;
  inputHeightSm: number;
  inputHeight: number;
  isLoading: boolean;
  multipleValue: number;
  tickerSymbol: string;
  chartWidth: number;
  preferredMultiple: PreferredMultiple;
  noirGradient01: string;
  industryCompanies: any;
}

const MultipleVsIndustryChart = ({
  size,
  isLoading,
  multipleValue,
  tickerSymbol,
  preferredMultiple,
  noirGradient01,
  industryCompanies,
  inputHeightSm,
  inputHeight,
  chartWidth,
}: Props) => {
  const theme = useTheme();
  const gutterTop = 8;
  const gutterLeft = 27;
  const gutterRight = 14;
  const rowMargin = 2;
  const yAxisSize = 4;
  const isSmall = Number(size.width) < theme.smMaxRaw;
  const condenseLabels = false;
  const height = isSmall ? inputHeightSm : inputHeight;
  const topMargin = isSmall ? 72 : 36; //Extra space for condensed labels
  const bottomMargin = 36;
  const chartHeight = height + topMargin - bottomMargin;
  const chartAreaWidth = chartWidth - gutterRight - gutterLeft;
  const padding = 8;
  const companyLetterWidth = 8;
  const industryAvgLetterWidth = 6.5;

  const { columnWidth, xScale, xScaleColumn, yScale, keys } = useMemo(() => {
    const { maxCount, max, companies, bucketSize } = industryCompanies;
    const columnWidth = chartAreaWidth / companies.length;
    const xScale = scaleLinear<number>({
      range: [gutterLeft, chartWidth - gutterRight],
      domain: [0, max + bucketSize],
    });
    const xScaleColumn = scaleLinear<number>({
      range: [gutterLeft, chartWidth - gutterRight],
      domain: [0, companies.length],
    });
    const yScale = scaleLinear<number>({
      range: [chartHeight, gutterTop + topMargin],
      domain: [0, maxCount],
    });

    const keys = industryCompanies.companies.map((c, i) =>
      i === industryCompanies.companies.length - 1
        ? industryCompanies.max + industryCompanies.bucketSize
        : (c.to as number)
    );
    return {
      columnWidth,
      xScale,
      xScaleColumn,
      yScale,
      keys,
    };
  }, [industryCompanies, chartAreaWidth, chartWidth, topMargin, chartHeight]);

  const { median, bucketSize, maxCount, companies } = industryCompanies;
  const medianX = xScale(median || 0);
  const medianXPercent = (medianX * 100) / chartWidth;
  const companyX = xScale(multipleValue);
  let companyY = topMargin;
  let labelOverlap = false;
  const companyLabelWidth = getLabelWidth(
    condenseLabels,
    formatMultiple(multipleValue).length,
    tickerSymbol.length,
    padding,
    companyLetterWidth
  );
  const medianLabelWidth = getLabelWidth(
    condenseLabels,
    formatMultiple(median).length,
    'Industry Avg'.length,
    padding,
    industryAvgLetterWidth
  );

  // Company Label on the left
  if (medianX >= companyX && multipleValue > 0) {
    // Check for overlap
    if (companyLabelWidth > medianX) {
      companyY = companyY - bottomMargin;
      labelOverlap = true;
    }
    // Company Label on the right
  } else if (companyX > medianX && multipleValue > 0) {
    // Check for overlap
    if (medianLabelWidth > companyX) {
      companyY = companyY - bottomMargin;
      labelOverlap = true;
    }
  }
  // Unprofitable
  if (multipleValue < 0) {
    const unprofitableLabel = `${tickerSymbol} is unprofitable`;
    const unprofitableLabelLength = unprofitableLabel.length * 6 + 16;
    const unprofitableLabelX = chartWidth - unprofitableLabelLength;
    if (medianX + medianLabelWidth > unprofitableLabelX) {
      labelOverlap = true;
    }
  }

  const t = useValuationTranslation();

  return (
    <MultipleVsIndustryChartSvg>
      {typeof median === 'number' && !isLoading && (
        <>
          <PassFailBackground
            y={topMargin}
            height={height - bottomMargin}
            percent={medianXPercent}
          />
          {/* COMPANY LABELS */}
          {multipleValue > 0 ? (
            <Label
              data-cy-id="vs-industry-company-label"
              x={companyX}
              y={companyY}
              multiple={multipleValue}
              fill={theme.color.chart01}
              height={labelOverlap ? height : height - bottomMargin}
              lineWidth={1}
              title={tickerSymbol}
              anchor={medianX >= companyX ? 'end' : 'start'}
              chartWidth={chartWidth}
              condensed={condenseLabels}
            />
          ) : (
            <UnprofitableLabel
              tickerSymbol={tickerSymbol}
              letterWidth={6}
              y={topMargin}
              chartWidth={chartWidth}
              condensed={condenseLabels}
              liftUp={labelOverlap}
            />
          )}
          {/* AVG LABELS */}
          <Label
            data-cy-id="vs-industry-avg-label"
            x={medianX}
            y={topMargin}
            multiple={median}
            fill={theme.color.neutral}
            fontColor={theme.color.noir}
            height={height - bottomMargin}
            lineWidth={2}
            title={t('vsIndustry.chart.industryAverage')}
            anchor={medianX >= companyX ? 'start' : 'end'}
            letterWidth={industryAvgLetterWidth}
            chartWidth={chartWidth}
            condensed={condenseLabels}
          />
        </>
      )}
      {/* Y AXIS LINES */}
      {isLoading
        ? null
        : Array.from({ length: yAxisSize }).map((_, i) => {
            return (
              <rect
                key={i}
                width="100%"
                height={1}
                y={yScale((i + 1) * (maxCount / yAxisSize))}
                fill={theme.color.whiteRGBA(0.1)}
              />
            );
          })}
      {/* Y AXIS LABELS */}
      <text
        x={15}
        y={chartHeight - 8}
        fill={theme.color.white}
        fontSize={11}
        transform={`rotate(-90 15 ${chartHeight - 8})`}
      >
        No. of Companies
      </text>
      {isLoading ? null : (
        <Text x={12} y={topMargin + gutterTop + 12}>
          {maxCount}
        </Text>
      )}
      {/* X AXIS  */}
      <g>
        <rect
          width="100%"
          y={chartHeight}
          height={1}
          fill={theme.color.whiteRGBA(0.2)}
        />
        <Text x={8} y={chartHeight + 20}>
          {preferredMultiple.toUpperCase()}
        </Text>
        {Array.from({ length: companies.length + 1 }).map((_, i) => {
          return (
            <g key={i} transform={`translate(${xScaleColumn(i)},0)`}>
              <rect
                y={chartHeight + 1}
                width={1}
                height={4}
                fill={theme.color.whiteRGBA(0.2)}
                transform={`translate(-1,0)`}
              />
              {i % 4 === 0 && (
                <Text y={chartHeight + 20}>
                  {`${toSignificant(i * bucketSize, 1)}${
                    i === companies.length - 1 ? '+' : ''
                  }`}
                </Text>
              )}
            </g>
          );
        })}
      </g>
      {/* COLUMNS */}
      {companies.map((bucket, i) => {
        const { key, count, from, to } = bucket;
        const columnHeight = yScale(Math.min(maxCount, count)); //Do not exceed max count
        const selected =
          i === companies.length - 1
            ? multipleValue >= Number(from)
            : multipleValue >= Number(from) && multipleValue < Number(to);
        return (
          <g transform={`translate(${xScaleColumn(i)},0)`} key={key}>
            <rect
              fill={selected ? theme.color.chart01 : noirGradient01}
              y={columnHeight}
              width={Math.max(columnWidth - rowMargin, 0)}
              height={chartHeight - columnHeight}
            />
            {count > 0 && (
              <rect
                fill={theme.color.white}
                y={columnHeight}
                width={Math.max(columnWidth - rowMargin, 0)}
                height={2}
              />
            )}
          </g>
        );
      })}
      {isLoading ? null : (
        <ChartInteractions
          x={gutterLeft}
          y={topMargin}
          width={chartWidth - gutterRight - gutterLeft}
          height={height - bottomMargin}
          keys={keys}
          xScaleColumn={xScaleColumn}
          xScale={xScale}
          columnWidth={columnWidth}
          rowMargin={rowMargin}
        />
      )}
    </MultipleVsIndustryChartSvg>
  );
};
export default MultipleVsIndustryChart;
